import type { PaidMediaOverviewPlatform } from "src/services/PaidMediaOverviewAPI";

/**
 * Single source of truth for the platforms the Paid Media Overview renders, so
 * every card, chart series and share bar is driven by one list. `slug` is the
 * token the SCSS keys its per-platform colours on (`styles/_platforms.scss`).
 */
export type OverviewPlatformSlug = "meta" | "google" | "tiktok" | "snapchat";

export interface OverviewPlatformDescriptor {
  key: PaidMediaOverviewPlatform;
  slug: OverviewPlatformSlug;
  labelKey: string;
  /** `paidMediaOverview.tooltips.*` id for the platform summary card. */
  tooltipId: string;
  /** Spend series / share-bar colour. */
  color: string;
  /** Revenue series colour — a lighter tint of `color`. */
  revenueColor: string;
  /** Funnel stage 2 — each network names its click metric differently. */
  clicksLabelKey: string;
  /** Funnel stage 3 — TikTok reports purchases as "Complete Payments". */
  purchasesLabelKey: string;
  /** No live connector in this build: numbers come from the mock dataset. */
  mockOnly: boolean;
}

export const OVERVIEW_PLATFORM_DESCRIPTORS: OverviewPlatformDescriptor[] = [
  {
    key: "meta_ads",
    slug: "meta",
    labelKey: "paidMediaOverview.platforms.meta",
    tooltipId: "metaOverview",
    color: "#2b74e4",
    revenueColor: "#5d97ee",
    clicksLabelKey: "paidMediaOverview.metrics.linkClicks",
    purchasesLabelKey: "paidMediaOverview.metrics.purchases",
    mockOnly: false,
  },
  {
    key: "google_ads",
    slug: "google",
    labelKey: "paidMediaOverview.platforms.google",
    tooltipId: "googleOverview",
    color: "#f6c15f",
    revenueColor: "#f0a84a",
    clicksLabelKey: "paidMediaOverview.metrics.clicks",
    purchasesLabelKey: "paidMediaOverview.metrics.purchases",
    mockOnly: false,
  },
  {
    key: "tiktok_ads",
    slug: "tiktok",
    labelKey: "paidMediaOverview.platforms.tiktok",
    tooltipId: "tiktokOverview",
    color: "#ff3b5c",
    revenueColor: "#ff8098",
    clicksLabelKey: "paidMediaOverview.metrics.destinationClicks",
    purchasesLabelKey: "paidMediaOverview.metrics.completePayments",
    mockOnly: true,
  },
  {
    key: "snapchat_ads",
    slug: "snapchat",
    labelKey: "paidMediaOverview.platforms.snapchat",
    tooltipId: "snapchatOverview",
    color: "#0ea5a4",
    revenueColor: "#58c6bf",
    clicksLabelKey: "paidMediaOverview.metrics.swipeUps",
    purchasesLabelKey: "paidMediaOverview.metrics.purchases",
    mockOnly: true,
  },
];

export const OVERVIEW_PLATFORM_KEYS: PaidMediaOverviewPlatform[] =
  OVERVIEW_PLATFORM_DESCRIPTORS.map(descriptor => descriptor.key);

const DESCRIPTOR_BY_KEY = new Map<PaidMediaOverviewPlatform, OverviewPlatformDescriptor>(
  OVERVIEW_PLATFORM_DESCRIPTORS.map(descriptor => [descriptor.key, descriptor])
);

export function overviewPlatformDescriptor(
  platform: PaidMediaOverviewPlatform | null | undefined
): OverviewPlatformDescriptor | undefined {
  return platform ? DESCRIPTOR_BY_KEY.get(platform) : undefined;
}

export function overviewPlatformSlug(
  platform: PaidMediaOverviewPlatform | null | undefined
): OverviewPlatformSlug | "unknown" {
  return overviewPlatformDescriptor(platform)?.slug ?? "unknown";
}

